import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share, MoreVertical, Smartphone, Loader2 } from 'lucide-react';
import { getDb } from '../db';

/**
 * Landing route — decides where to go based on local device state:
 *   no device record  → /pair   (one-time email + password + PIN)
 *   device record set → /unlock (PIN or WebAuthn)
 *
 * Standalone-mode gate: iOS Safari only honors `navigator.storage.persist()`
 * when the PWA is installed to the home screen. We detect non-standalone
 * launch and show an "Add to Home Screen" prompt before pairing.
 *
 * Premium polish: brand mark + platform-aware 3-step install card.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const [installPrompt, setInstallPrompt] = useState<boolean | null>(null);
  const platform = useMemo<'ios' | 'android' | 'other'>(() => {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'other';
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      if (!standalone && !import.meta.env.DEV) {
        if (!cancelled) setInstallPrompt(true);
        return;
      }
      try {
        const db = getDb();
        const dev = await db.device.get('self');
        if (cancelled) return;
        if (dev == null) navigate('/pair', { replace: true });
        else navigate('/unlock', { replace: true });
      } catch {
        if (!cancelled) navigate('/pair', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (installPrompt) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <BrandMark />
        <h1 className="text-2xl font-bold text-mono-100 tracking-tight mt-6">Install ARGOS Field</h1>
        <p className="text-mono-400 text-sm text-center mt-2 max-w-xs">
          Add this app to your home screen so saved readings survive offline.
        </p>

        <div className="mt-6 w-full max-w-sm rounded-xl border border-mono-800 bg-mono-900/50 p-5 space-y-4">
          {platform === 'ios' && <IosSteps />}
          {platform === 'android' && <AndroidSteps />}
          {platform === 'other' && (
            <p className="text-mono-300 text-sm">
              Look for an "Install app" or "Add to home screen" option in your browser menu.
            </p>
          )}
        </div>

        <p className="text-mono-500 text-xs text-center mt-5 max-w-xs">
          You'll only need to do this once. After install, tap the ARGOS Field icon on your home screen to launch.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
      <BrandMark />
      <div className="flex items-center gap-2 text-mono-400 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
        <span>Loading…</span>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="flex flex-col items-center">
      <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-cyan-700 shadow-glow flex items-center justify-center">
        <Smartphone className="h-7 w-7 text-mono-950" strokeWidth={2.4} />
      </div>
      <p className="mt-3 text-[11px] tracking-[0.3em] uppercase text-mono-400">ARGOS Field</p>
    </div>
  );
}

function StepRow({ n, icon, label }: { n: number; icon: React.ReactNode; label: React.ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="h-6 w-6 shrink-0 rounded-full bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/40 inline-flex items-center justify-center text-[11px] font-semibold">
        {n}
      </span>
      <span className="text-mono-200 text-sm flex items-center gap-1.5 leading-snug">{icon}{label}</span>
    </li>
  );
}

function IosSteps() {
  return (
    <ol className="space-y-3">
      <StepRow n={1} icon={<Share className="h-4 w-4 text-cyan-300" strokeWidth={2} />} label={<>Tap the <span className="font-semibold">Share</span> button in Safari</>} />
      <StepRow n={2} icon={<span className="text-cyan-300">＋</span>} label={<>Scroll and tap <span className="font-semibold">Add to Home Screen</span></>} />
      <StepRow n={3} icon={<Smartphone className="h-4 w-4 text-cyan-300" strokeWidth={2} />} label={<>Open ARGOS Field from your home screen</>} />
    </ol>
  );
}

function AndroidSteps() {
  return (
    <ol className="space-y-3">
      <StepRow n={1} icon={<MoreVertical className="h-4 w-4 text-cyan-300" strokeWidth={2} />} label={<>Open Chrome's menu (the <span className="font-semibold">⋮</span> icon)</>} />
      <StepRow n={2} icon={<span className="text-cyan-300">↧</span>} label={<>Tap <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to home screen</span></>} />
      <StepRow n={3} icon={<Smartphone className="h-4 w-4 text-cyan-300" strokeWidth={2} />} label={<>Open ARGOS Field from your home screen</>} />
    </ol>
  );
}
